import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useAction } from "convex/react";
import { useState } from "react";
import { toast } from "sonner";

import { stripeTerminalDeleteReader } from "~/api-refs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Spinner } from "~/components/ui/spinner";
import { logger } from "~/lib/logger";
import { requiredSearchString } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/terminals/readers/$readerId/delete"
)({
  component: DeleteReaderModal,
  validateSearch: (search: Record<string, unknown>) => ({
    label: requiredSearchString(search.label),
    deviceType: requiredSearchString(search.deviceType, "Card Reader"),
  }),
});

function DeleteReaderModal() {
  const navigate = useNavigate();
  const { readerId } = useParams({
    from: "/_authenticated/settings/terminals/readers/$readerId/delete",
  });
  const { label, deviceType } = useSearch({
    from: "/_authenticated/settings/terminals/readers/$readerId/delete",
  });

  const deleteReader = useAction(stripeTerminalDeleteReader);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    void navigate({ to: "/settings/terminals" });
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      await deleteReader({ readerId });
      ProductEvents.readerDeleted(readerId);
      toast.success("Card reader deleted successfully");
      handleClose();
    } catch (error) {
      logger.exception("reader_delete_failed", error, { readerId });
      toast.error(
        error instanceof Error ? error.message : "Failed to delete card reader"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const readerName = label || deviceType;

  return (
    <AlertDialog onOpenChange={(open: boolean) => !open && handleClose()} open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Card Reader</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{readerName}"? This action cannot
            be undone. This card reader will need to be re-registered before it
            can be used again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isDeleting}
            onClick={handleDelete}
          >
            {isDeleting ? (
              <>
                <Spinner className="mr-2 size-4" />
                Deleting...
              </>
            ) : (
              "Delete Card Reader"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
